import { PaymentProviderType } from "@redotech/redo-billing-common/billing-engine-types";
import { UsdCents } from "@redotech/redo-billing-common/currency";
import { DisputesBillingSettings } from "@redotech/redo-billing-common/disputes/disputes-billing-types";
import { BillingPeriod } from "@redotech/redo-billing-common/service-invoice";
import {
  DisputesTrialSettings,
  DisputesTrialSource,
} from "@redotech/redo-dispute-settings-common/disputes-settings";
import type {
  DisputesAccessOverride,
  ReclaimAccessDefaults,
  ReclaimAccessMode,
  ReclaimPricingOverride,
  ReclaimPricingTerms,
} from "@redotech/redo-dispute-settings-common/reclaim-access-settings";
import {
  DEFAULT_RECLAIM_ACCESS_DEFAULTS,
  DEFAULT_RECLAIM_MIN_FEE_DOLLARS,
  getReclaimSourceMaterialization,
  resolveReclaimCommercialTerms,
} from "@redotech/redo-dispute-settings-common/reclaim-access-settings";
import { Temporal } from "temporal-polyfill";

export const DISPUTES_TRIAL_EXPIRING_SOON_DAYS = 60;

export type DisputesAccessState =
  | "disabled"
  | "automation_off"
  | "free_access"
  | "trial_active"
  | "billing_ready"
  | "billing_active"
  | "billing_required";

export interface DisputesAccessStatus {
  state: DisputesAccessState;
  accessMode: ReclaimAccessMode | "paid";
  canUseAutomation: boolean;
  requiresBilling: boolean;
  billingAccepted: boolean;
  freeAccessEnabled: boolean;
  trialStartedAt: Date | null;
  trialEndsAt: Date | null;
  trialDaysRemaining: number | null;
  trialMonthsRemaining: number | null;
  trialExpiringSoon: boolean;
}

export type DisputesEvidenceProcessingBlockedReason =
  | "billing_required"
  | "product_disabled";

export interface DisputesPricingOverrideShape {
  successFeePercentage?: number | null;
  minFeeDollars?: number | null;
  source?: string | null;
  appliedAt?: Date | string | null;
}

export interface DisputesStatusTeam {
  settings?: {
    disputes?: {
      enabled?: boolean | null;
      automateEvidenceEnabled?: boolean | null;
      accessOverride?: DisputesAccessOverride | null;
      freeAccessEnabled?: boolean | null;
      billing?: {
        enabled?: boolean | null;
        startDate?: Date | string | null;
        period?: BillingPeriod | string | null;
        periodFeeInCents?: number | null;
        renewOnPeriodEnd?: boolean | null;
        paymentProvider?: string | null;
        feePercentage?: number | null;
        minFeeDollars?: number | null;
      } | null;
      pricingOverride?: DisputesPricingOverrideShape | null;
      reclaimShopifyAppInstalledAt?: Date | string | null;
      trial?: DisputesTrialSettings | null;
      evidenceWindowDays?: number | null;
    } | null;
  } | null;
}

type DisputesSettingsShape = NonNullable<
  NonNullable<DisputesStatusTeam["settings"]>["disputes"]
>;

type ReclaimInstallBillingSettings = Omit<
  DisputesBillingSettings,
  "feePercentage" | "minFeeDollars" | "periodFeeInCents"
> & { periodFeeInCents: number } & Partial<
    Pick<DisputesBillingSettings, "feePercentage" | "minFeeDollars">
  >;

export interface ReclaimInstallDisputesSettings {
  enabled?: boolean;
  automateEvidenceEnabled?: boolean;
  accessOverride?: DisputesAccessOverride;
  freeAccessEnabled?: boolean;
  billing?: ReclaimInstallBillingSettings;
  pricingOverride?: ReclaimPricingOverride;
  reclaimShopifyAppInstalledAt?: Date;
  trial?: DisputesTrialSettings;
  evidenceWindowDays?: number;
}

interface ValidDisputesTrial {
  startedAt: Date;
  endsAt: Date;
  source: DisputesTrialSource;
}

export function createDisputesTrial(
  startedAt: Date,
  source: DisputesTrialSource,
  trialMonths = DEFAULT_RECLAIM_ACCESS_DEFAULTS.trialMonths,
): Required<DisputesTrialSettings> {
  return {
    startedAt,
    endsAt: calculateDisputesTrialEndDate(startedAt, trialMonths),
    source,
  };
}

export function calculateDisputesTrialEndDate(
  startedAt: Date,
  trialMonths = DEFAULT_RECLAIM_ACCESS_DEFAULTS.trialMonths,
): Date {
  const instant = Temporal.Instant.from(startedAt.toISOString());
  const endsAt = instant
    .toZonedDateTimeISO("UTC")
    .add({ months: trialMonths })
    .toInstant();
  return new Date(endsAt.epochMilliseconds);
}

export function getDisputesAccessStatus(
  team: DisputesStatusTeam | null | undefined,
  now: Date,
  accessDefaults: ReclaimAccessDefaults = DEFAULT_RECLAIM_ACCESS_DEFAULTS,
): DisputesAccessStatus {
  const disputes = team?.settings?.disputes;
  const accessMode = getEffectiveDisputesAccessMode(disputes, accessDefaults);
  const billing = disputes?.billing;
  const trial = getValidTrial(disputes?.trial);
  const trialRemaining = getTrialRemaining(trial?.endsAt ?? null, now);
  const freeAccessEnabled = accessMode === "free";
  const billingAccepted = billing?.enabled === true && !freeAccessEnabled;
  const billingStartDate = validDateOrNull(billing?.startDate);
  const billingActive =
    billingAccepted && (!billingStartDate || billingStartDate <= now);
  const billingReady = billingAccepted && !billingActive;
  const trialActive = !!trial && trial.endsAt > now;
  const requiresBilling =
    !billingAccepted &&
    (accessMode === "paid" || (accessMode === "trial" && !trialActive));

  if (disputes?.enabled !== true) {
    return buildStatus("disabled", {
      accessMode,
      billingAccepted,
      canUseAutomation: false,
      freeAccessEnabled,
      requiresBilling: false,
      trial,
      trialRemaining,
    });
  }

  if (requiresBilling) {
    return buildStatus("billing_required", {
      accessMode,
      billingAccepted,
      canUseAutomation: false,
      freeAccessEnabled,
      requiresBilling: true,
      trial,
      trialRemaining,
    });
  }

  if (disputes.automateEvidenceEnabled !== true) {
    return buildStatus("automation_off", {
      accessMode,
      billingAccepted,
      canUseAutomation: false,
      freeAccessEnabled,
      requiresBilling: false,
      trial,
      trialRemaining,
    });
  }

  if (freeAccessEnabled) {
    return buildStatus("free_access", {
      accessMode,
      billingAccepted,
      canUseAutomation: true,
      freeAccessEnabled,
      requiresBilling: false,
      trial,
      trialRemaining,
    });
  }

  if (billingActive) {
    return buildStatus("billing_active", {
      accessMode,
      billingAccepted,
      canUseAutomation: true,
      freeAccessEnabled,
      requiresBilling: false,
      trial,
      trialRemaining,
    });
  }

  if (billingReady) {
    return buildStatus("billing_ready", {
      accessMode,
      billingAccepted,
      canUseAutomation: true,
      freeAccessEnabled,
      requiresBilling: false,
      trial,
      trialRemaining,
    });
  }

  if (trialActive) {
    return buildStatus("trial_active", {
      accessMode,
      billingAccepted,
      canUseAutomation: true,
      freeAccessEnabled,
      requiresBilling: false,
      trial,
      trialRemaining,
    });
  }

  return buildStatus("billing_required", {
    accessMode,
    billingAccepted,
    canUseAutomation: false,
    freeAccessEnabled,
    requiresBilling: true,
    trial,
    trialRemaining,
  });
}

export function canUseDisputesAutomation(
  team: DisputesStatusTeam | null | undefined,
  now: Date,
  accessDefaults: ReclaimAccessDefaults = DEFAULT_RECLAIM_ACCESS_DEFAULTS,
): boolean {
  return getDisputesAccessStatus(team, now, accessDefaults).canUseAutomation;
}

export function canGenerateDisputesEvidence(
  team: DisputesStatusTeam | null | undefined,
  now: Date,
  accessDefaults: ReclaimAccessDefaults = DEFAULT_RECLAIM_ACCESS_DEFAULTS,
): boolean {
  return (
    getDisputesEvidenceProcessingBlockedReason(team, now, accessDefaults) ==
    null
  );
}

export function canProcessDisputesEvidence(
  team: DisputesStatusTeam | null | undefined,
  now: Date,
  accessDefaults: ReclaimAccessDefaults = DEFAULT_RECLAIM_ACCESS_DEFAULTS,
): boolean {
  return (
    getDisputesEvidenceProcessingBlockedReason(team, now, accessDefaults) ==
    null
  );
}

export function getDisputesEvidenceProcessingBlockedReason(
  team: DisputesStatusTeam | null | undefined,
  now: Date,
  accessDefaults: ReclaimAccessDefaults = DEFAULT_RECLAIM_ACCESS_DEFAULTS,
): DisputesEvidenceProcessingBlockedReason | null {
  const status = getDisputesAccessStatus(team, now, accessDefaults);
  if (status.state === "disabled") {
    return "product_disabled";
  }
  if (status.requiresBilling) {
    return "billing_required";
  }
  return null;
}

export function needsDisputesTrialBootstrap(
  team: DisputesStatusTeam | null | undefined,
  now: Date,
  accessDefaults: ReclaimAccessDefaults,
): boolean {
  const status = getDisputesAccessStatus(team, now, accessDefaults);
  return (
    team?.settings?.disputes?.enabled === true &&
    status.accessMode === "trial" &&
    !status.billingAccepted &&
    !status.freeAccessEnabled &&
    !status.trialEndsAt
  );
}

export function buildDisputesBillingSettings({
  now,
  trialEndsAt,
  accessDefaults,
  pricingTerms,
}: {
  now: Date;
  trialEndsAt?: Date | null;
  accessDefaults: ReclaimAccessDefaults;
  pricingTerms?: ReclaimPricingTerms;
}): DisputesBillingSettings {
  const startDate =
    trialEndsAt && validDateOrNull(trialEndsAt) && trialEndsAt > now
      ? trialEndsAt
      : now;
  const pricing = pricingTerms ?? {
    successFeePercentage: accessDefaults.successFeePercentage,
    minFeeDollars:
      accessDefaults.minFeeDollars ?? DEFAULT_RECLAIM_MIN_FEE_DOLLARS,
  };
  return {
    enabled: true,
    startDate,
    period: BillingPeriod.MONTHLY,
    periodFeeInCents: 0 as UsdCents,
    renewOnPeriodEnd: true,
    minFeeDollars: pricing.minFeeDollars,
    feePercentage: pricing.successFeePercentage,
  };
}

export function getEffectiveDisputesAccessMode(
  disputes:
    | {
        accessOverride?: DisputesAccessOverride | null;
        freeAccessEnabled?: boolean | null;
      }
    | null
    | undefined,
  accessDefaults: ReclaimAccessDefaults,
): ReclaimAccessMode | "paid" {
  return resolveReclaimCommercialTerms({ accessDefaults, disputes }).accessMode;
}

export function getEffectiveDisputesPricing(
  disputes:
    | {
        pricingOverride?: DisputesPricingOverrideShape | null;
        billing?: {
          feePercentage?: number | null;
          minFeeDollars?: number | null;
        } | null;
      }
    | null
    | undefined,
  accessDefaults: ReclaimAccessDefaults,
): ReclaimPricingTerms {
  const terms = resolveReclaimCommercialTerms({ accessDefaults, disputes });
  return {
    successFeePercentage: terms.successFeePercentage,
    minFeeDollars: terms.minFeeDollars,
  };
}

export function buildReclaimInstallDisputesSettings({
  accessDefaults,
  existingDisputes,
  now,
}: {
  accessDefaults: ReclaimAccessDefaults;
  existingDisputes?: DisputesSettingsShape | null;
  now: Date;
}): ReclaimInstallDisputesSettings {
  const disputes = compactDisputesSettings(existingDisputes);
  const materialization = getReclaimSourceMaterialization(
    accessDefaults,
    "shopify_reclaim_install",
    now,
  );
  const hasMerchantAccessOverride =
    (disputes.accessOverride != null &&
      disputes.accessOverride !== "default") ||
    disputes.freeAccessEnabled === true;
  // OR (not AND): a team with only one pricingOverride subfield set (e.g.,
  // an admin set minFeeDollars but not successFeePercentage, or vice versa)
  // already counts as having a merchant pricing decision. Clobbering that
  // partial override with a materialized full one would silently discard
  // the admin's choice. Matches the activation-path semantics in
  // buildReclaimSourceMaterializationPlan (disputes-trial.ts) and the
  // backfill script's skip guard.
  const hasMerchantPricingOverride =
    disputes.pricingOverride?.successFeePercentage != null ||
    disputes.pricingOverride?.minFeeDollars != null;

  // Full materialization: always stamp concrete access and pricing from the
  // current globals. Skip only when the merchant already carries an override
  // (manual admin edit or a prior materialization) — we never clobber an
  // existing decision.
  if (!hasMerchantAccessOverride) {
    disputes.accessOverride = materialization.accessOverride;
  }
  if (!hasMerchantPricingOverride) {
    disputes.pricingOverride = materialization.pricingOverride;
  }

  const terms = resolveReclaimCommercialTerms({ accessDefaults, disputes });
  const trial = getValidTrial(disputes.trial);
  const shouldKeepExistingTrialOrSkipTrial =
    trial != null ||
    disputes.billing?.enabled === true ||
    terms.accessMode === "free" ||
    terms.accessMode === "paid";

  return {
    ...disputes,
    enabled: true,
    reclaimShopifyAppInstalledAt: disputes.reclaimShopifyAppInstalledAt ?? now,
    trial: shouldKeepExistingTrialOrSkipTrial
      ? disputes.trial
      : createDisputesTrial(now, "reclaim_app_install", terms.trialMonths),
  };
}

function compactDisputesSettings(
  existingDisputes: DisputesSettingsShape | null | undefined,
): ReclaimInstallDisputesSettings {
  if (!existingDisputes) {
    return {};
  }
  const compacted: ReclaimInstallDisputesSettings = {};
  if (existingDisputes.enabled != null) {
    compacted.enabled = existingDisputes.enabled;
  }
  if (existingDisputes.automateEvidenceEnabled != null) {
    compacted.automateEvidenceEnabled =
      existingDisputes.automateEvidenceEnabled;
  }
  if (existingDisputes.accessOverride != null) {
    compacted.accessOverride = existingDisputes.accessOverride;
  }
  if (existingDisputes.freeAccessEnabled != null) {
    compacted.freeAccessEnabled = existingDisputes.freeAccessEnabled;
  }
  const billing = compactBillingSettings(existingDisputes.billing);
  if (billing) {
    compacted.billing = billing;
  }
  const pricingOverride = compactPricingOverride(
    existingDisputes.pricingOverride,
  );
  if (pricingOverride) {
    compacted.pricingOverride = pricingOverride;
  }
  if (existingDisputes.trial != null) {
    compacted.trial = existingDisputes.trial;
  }
  const reclaimShopifyAppInstalledAt = validDateOrNull(
    existingDisputes.reclaimShopifyAppInstalledAt,
  );
  if (reclaimShopifyAppInstalledAt != null) {
    compacted.reclaimShopifyAppInstalledAt = reclaimShopifyAppInstalledAt;
  }
  if (existingDisputes.evidenceWindowDays != null) {
    compacted.evidenceWindowDays = existingDisputes.evidenceWindowDays;
  }
  return compacted;
}

function compactBillingSettings(
  billing: DisputesSettingsShape["billing"],
): ReclaimInstallBillingSettings | undefined {
  const period = compactBillingPeriod(billing?.period);
  if (
    !billing ||
    billing.enabled == null ||
    period == null ||
    billing.periodFeeInCents == null ||
    billing.renewOnPeriodEnd == null
  ) {
    return undefined;
  }
  const compacted: ReclaimInstallBillingSettings = {
    enabled: billing.enabled,
    period,
    periodFeeInCents: billing.periodFeeInCents,
    renewOnPeriodEnd: billing.renewOnPeriodEnd,
    startDate: validDateOrNull(billing.startDate),
  };
  if (
    billing.paymentProvider === PaymentProviderType.SHOPIFY ||
    billing.paymentProvider === PaymentProviderType.STRIPE
  ) {
    compacted.paymentProvider = billing.paymentProvider;
  }
  if (billing.feePercentage != null) {
    compacted.feePercentage = billing.feePercentage;
  }
  if (billing.minFeeDollars != null) {
    compacted.minFeeDollars = billing.minFeeDollars;
  }
  return compacted;
}

function compactBillingPeriod(
  period: BillingPeriod | string | null | undefined,
): BillingPeriod | undefined {
  switch (period) {
    case BillingPeriod.WEEKLY:
    case BillingPeriod.BIMONTHLY:
    case BillingPeriod.MONTHLY:
    case BillingPeriod.YEARLY:
      return period;
    default:
      return undefined;
  }
}

function compactPricingOverride(
  pricingOverride: DisputesPricingOverrideShape | null | undefined,
): ReclaimPricingOverride | undefined {
  // Preserve partial overrides — a team carrying only successFeePercentage
  // OR only minFeeDollars still represents an explicit merchant decision and
  // must not be silently dropped here. (Dropping it would let
  // hasMerchantPricingOverride see undefined and clobber it via
  // materialization.) Matches the OR semantics in
  // buildReclaimSourceMaterializationPlan and the migration backfill.
  if (
    pricingOverride?.successFeePercentage == null &&
    pricingOverride?.minFeeDollars == null
  ) {
    return undefined;
  }

  const compacted: ReclaimPricingOverride = {} as ReclaimPricingOverride;
  if (pricingOverride.successFeePercentage != null) {
    compacted.successFeePercentage = pricingOverride.successFeePercentage;
  }
  if (pricingOverride.minFeeDollars != null) {
    compacted.minFeeDollars = pricingOverride.minFeeDollars;
  }
  if (pricingOverride.source != null) {
    compacted.source = pricingOverride.source;
  }
  const appliedAt = validDateOrNull(pricingOverride.appliedAt);
  if (appliedAt) {
    compacted.appliedAt = appliedAt;
  }
  return compacted;
}

export function getDisputesBillableFrom({
  windowStartsAt,
  billingStartDate,
  trialEndsAt,
}: {
  windowStartsAt: Date;
  billingStartDate?: Date | null;
  trialEndsAt?: Date | null;
}): Date {
  return maxDate(
    windowStartsAt,
    validDateOrNull(billingStartDate),
    validDateOrNull(trialEndsAt),
  );
}

function buildStatus(
  state: DisputesAccessState,
  {
    accessMode,
    billingAccepted,
    canUseAutomation,
    freeAccessEnabled,
    requiresBilling,
    trial,
    trialRemaining,
  }: {
    accessMode: ReclaimAccessMode | "paid";
    billingAccepted: boolean;
    canUseAutomation: boolean;
    freeAccessEnabled: boolean;
    requiresBilling: boolean;
    trial: ValidDisputesTrial | null;
    trialRemaining: { days: number; months: number } | null;
  },
): DisputesAccessStatus {
  return {
    state,
    accessMode,
    billingAccepted,
    canUseAutomation,
    freeAccessEnabled,
    requiresBilling,
    trialStartedAt: trial?.startedAt ?? null,
    trialEndsAt: trial?.endsAt ?? null,
    trialDaysRemaining: trialRemaining?.days ?? null,
    trialMonthsRemaining: trialRemaining?.months ?? null,
    trialExpiringSoon:
      trialRemaining != null &&
      trialRemaining.days <= DISPUTES_TRIAL_EXPIRING_SOON_DAYS,
  };
}

function getValidTrial(
  trial: DisputesTrialSettings | null | undefined,
): ValidDisputesTrial | null {
  const startedAt = validDateOrNull(trial?.startedAt);
  const endsAt = validDateOrNull(trial?.endsAt);
  if (!startedAt || !endsAt || !trial?.source) {
    return null;
  }
  return { startedAt, endsAt, source: trial.source };
}

function getTrialRemaining(
  endsAt: Date | null,
  now: Date,
): { days: number; months: number } | null {
  if (!endsAt) {
    return null;
  }
  const millisecondsRemaining = Math.max(0, endsAt.getTime() - now.getTime());
  const days = Math.ceil(millisecondsRemaining / (24 * 60 * 60 * 1000));
  const nowDate = toUtcPlainDate(now);
  const endDate = toUtcPlainDate(endsAt);
  const duration = nowDate.until(endDate, { largestUnit: "months" });
  const months = Math.max(0, duration.months + (duration.days > 0 ? 1 : 0));
  return { days, months };
}

function toUtcPlainDate(date: Date): Temporal.PlainDate {
  return Temporal.Instant.from(date.toISOString())
    .toZonedDateTimeISO("UTC")
    .toPlainDate();
}

function validDateOrNull(date: Date | string | null | undefined): Date | null {
  if (!date) {
    return null;
  }
  const parsed = date instanceof Date ? date : new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function maxDate(first: Date, ...dates: (Date | null | undefined)[]): Date {
  let max = first;
  for (const date of dates) {
    if (date && date > max) {
      max = date;
    }
  }
  return max;
}

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  DEFAULT_RECLAIM_MIN_FEE_DOLLARS as DISPUTES_MIN_FEE_DOLLARS,
  DEFAULT_RECLAIM_SUCCESS_FEE_PERCENTAGE as DISPUTES_SUCCESS_FEE_PERCENTAGE,
  DEFAULT_RECLAIM_TRIAL_MONTHS as DISPUTES_TRIAL_MONTHS,
} from "@redotech/redo-dispute-settings-common/reclaim-access-settings";
